import type { LensCardData, LensFilterValues } from '~/types/leLens';

import catalogue from './catalogue.generated.json';

export const CS_DAILY_OPS_PAGE_ID = 'cs-daily-ops';

export type Preset = 'yesterday' | '7d' | 'wtd' | 'lastweek' | '30d' | 'mtd';
export const PRESETS: ReadonlyArray<{ value: Preset; label: string; note?: string }> = [
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: '7 days' },
  { value: 'wtd', label: 'This week', note: 'Monday to the last complete day' },
  { value: 'lastweek', label: 'Last week', note: 'The last complete Monday-to-Sunday week' },
  { value: '30d', label: '30 days' },
  { value: 'mtd', label: 'This month' },
];
export type Comparison = 'previous' | 'year';
export const COMPARISONS: ReadonlyArray<{ value: Comparison; label: string; note: string }> = [
  { value: 'previous', label: 'Previous period', note: 'The window shifted back by its own length.' },
  {
    value: 'year',
    label: 'Same period last year',
    note: 'The same dates one calendar year back; for a single day the weekday differs.',
  },
];
export type Tab = 'dashboard' | 'agents' | 'live';
export const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'agents', label: 'Agents' },
  { value: 'live', label: 'Live board' },
];
/** Spec filter ids, in bar order. Teams narrows the Team list (CS = Support AU / ATI / INTL). */
export const SELECTS = [
  { key: 'region', label: 'Region' },
  { key: 'department', label: 'Department' },
  { key: 'team', label: 'Team' },
] as const;
type SelectKey = (typeof SELECTS)[number]['key'];

export type Filters = {
  tab: Tab;
  preset: Preset;
  comparison: Comparison;
  selections: Record<SelectKey, string | null>;
};

const pick = <T extends string>(values: ReadonlyArray<T>, value: string | null, fallback: T): T =>
  values.includes(value as T) ? (value as T) : fallback;

export function parseFilters(search: URLSearchParams): Filters {
  return {
    tab: pick(
      TABS.map((tab) => tab.value),
      search.get('tab'),
      'dashboard',
    ),
    preset: pick(
      PRESETS.map((preset) => preset.value),
      search.get('period'),
      'yesterday',
    ),
    comparison: pick(
      COMPARISONS.map((comparison) => comparison.value),
      search.get('compare'),
      'previous',
    ),
    selections: Object.fromEntries(SELECTS.map(({ key }) => [key, search.get(key)])) as Filters['selections'],
  };
}

export function writeFilters(filters: Filters, search: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(search);
  next.set('tab', filters.tab);
  next.set('period', filters.preset);
  next.set('compare', filters.comparison);
  for (const { key } of SELECTS) {
    const value = filters.selections[key];
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
  }
  return next;
}

/** The Lens period grammar for each preset; weeks start on Monday per the spec calendar. */
export function presetWindow(preset: Preset) {
  switch (preset) {
    case 'yesterday':
      return { days: 1 };
    case '7d':
      return { days: 7 };
    case 'wtd':
      return { toDate: 'week' };
    case 'lastweek':
      return { calendar: 'week', back: 1 };
    case '30d':
      return { days: 30 };
    default:
      return { toDate: 'month' };
  }
}

export function lensRequest(filters: Filters): LensFilterValues {
  const values: LensFilterValues = {
    period: {
      anchor: 'yesterday',
      window: presetWindow(filters.preset),
      comparison: filters.comparison === 'year' ? { back: 1, unit: 'year' } : 'previous',
    },
  } as LensFilterValues;
  for (const { key } of SELECTS) {
    const value = filters.selections[key];
    if (value) {
      values[key] = [value];
    }
  }
  return values;
}

/* ---------- catalogue: what the spec's cards mean on the page ---------- */
export type Format = 'count' | 'percent' | 'seconds' | 'minutes' | 'hours' | 'number1';
export type Meaning = 'up' | 'down' | 'neutral';
export type Item = {
  column: string | null;
  label: string;
  format: Format;
  meaning: Meaning;
  target?: number;
  target_max?: number;
  report?: string;
  threshold_flag?: string;
  note?: string;
  /** short hover helper shown beside the figure's label */
  hint?: string;
  lagDays?: number;
};
export type CatalogueCard = {
  id: string;
  type: 'metric' | 'table' | 'bar';
  section: string;
  title: string;
  description?: string;
  datasetId: string;
  size?: string;
  items?: Array<Item>;
  derived?: Record<string, { ratio: { of: string; per: string }; format: Format }>;
  keyLabels?: Record<string, string>;
  order?: Array<string>;
  share?: boolean;
};
export type LayoutCard = {
  id: string;
  type: 'channels' | 'stats' | 'matrix' | 'stacked' | 'bar' | 'bars' | 'table';
  section: string;
  title: string;
  /** one short note for the whole card, shown as the info icon beside its title */
  info?: string;
  size?: string;
  description?: string;
  datasetId?: string;
  lagDays?: number;
  lensCardId?: string;
  rows?: Array<{
    id: string;
    icon: string;
    name: string;
    hero: string;
    heroLabel: string;
    boxes: Array<string>;
    meters?: Array<{ item: string; label: string }>;
    na?: Array<{ label: string; reason: string }>;
    label: string;
    /** an "i" next to the row label explains the row's reading window (e.g. FCR) */
    info?: string;
    cells?: Record<string, string | { na: string; text: string }>;
  }>;
  columns?: Array<{ id: string; label: string }>;
  items?: Array<{ id: string; label: string; na?: string | null; reason?: string | null; alt: boolean }>;
  itemIds?: Array<string>;
  view?: {
    x?: string;
    value?: string;
    values?: Array<string>;
    /** second grouping key: rows are x by x2 (e.g. region by day type) */
    x2?: string;
    series?: string;
    seriesOrder?: Array<string>;
    order?: Array<string | number>;
    labels?: Array<string> | Record<string, string>;
    /** bar colour per category; categories not listed use the first series colour */
    colors?: Record<string, string>;
    share?: boolean;
    aggregate?: string;
    formats?: Record<string, Format>;
    noTotal?: Array<string>;
    guards?: Record<string, string>;
    /** rows whose x ends with suffix are listed under label below the total, and left out of it */
    separate?: { suffix: string; label: string; note?: string };
    derived?: Record<string, { ratio: { of: string; per: string }; format: Format }>;
    pivots?: Record<string, { column: string; equals: string; value: string }>;
    where?: Array<{ column: string; in?: Array<string>; notIn?: Array<string> }>;
  };
};
export type ItemRef = Item & {
  cardId: string;
  column?: string;
  ratio?: { of: string; per: string };
  /** a pooled median from bucket counts: columns[i] counts values in [edges[i], edges[i + 1]) */
  buckets?: { columns: Array<string>; edges: Array<number> };
  alt: boolean;
};

/** Median from bucket counts, interpolated inside the bucket that holds the middle value. */
export function bucketMedian(
  row: Record<string, unknown> | undefined,
  buckets: { columns: Array<string>; edges: Array<number> },
) {
  if (!row) {
    return null;
  }
  const counts = buckets.columns.map((c) => num(row[c]) ?? 0);
  const total = counts.reduce((a, b) => a + b, 0);
  if (!total) {
    return null;
  }
  let below = 0;
  for (let i = 0; i < counts.length; i += 1) {
    if (below + counts[i] >= total / 2) {
      const lo = buckets.edges[i];
      const hi = buckets.edges[i + 1];
      return hi === undefined ? lo : lo + ((total / 2 - below) / counts[i]) * (hi - lo);
    }
    below += counts[i];
  }
  return null;
}
export const CATALOGUE = catalogue as unknown as {
  sections: Array<{ id: string; title: string }>;
  cards: Array<CatalogueCard>;
  layout: Array<LayoutCard>;
  itemIndex: Record<string, ItemRef>;
  columnLabels: Record<string, Record<string, string>>;
  live: Array<{
    id: string;
    title: string;
    tiles: Array<{
      id: string;
      label: string;
      src?: string;
      ratio?: [string, string];
      format?: Format;
      feas: 'exists_hourly' | 'doable_hourly' | 'needs_5min';
      why?: string;
      good?: number;
      warn?: number;
      good_max?: number;
      warn_max?: number;
      agg?: 'max';
    }>;
  }>;
  agentsTab: {
    note: string;
    groups: Array<string>;
    columns: Array<{
      id: string;
      label: string;
      kind: 'sum' | 'ratio' | 'fcr';
      of: string;
      per?: string;
      format?: Format;
      countLabel?: string;
      group: string;
      meaning?: Meaning;
    }>;
  };
};
/** Dashboard sections: only those with a dashboard card (agents, live and the hidden filter data have their own homes). */
export const SECTIONS = CATALOGUE.sections.filter((section) =>
  CATALOGUE.layout.some((layout) => layout.section === section.id),
);
export const cardsIn = (section: string) => CATALOGUE.cards.filter((card) => card.section === section);

/* ---------- values ---------- */
/** Header and label text in sentence case: known column names, else the raw key with its first letter capitalised. */
const COLUMN_NAMES: Record<string, string> = {
  region: 'Region',
  team: 'Team',
  department: 'Department',
  channel: 'Channel',
  age_band: 'Age band',
  outcome_7d: '7-day outcome',
  region_row: 'Region',
  wait_leg: 'Wait leg',
  priority: 'Priority',
  csat_score: 'CSAT score',
  hour: 'Hour',
  agent_key: 'Agent',
};
export const sentence = (value: string): string => {
  if (!value) {
    return value;
  }
  if (COLUMN_NAMES[value]) {
    return COLUMN_NAMES[value];
  }
  const spaced = value.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(typeof value === 'object' && value && 'value' in value ? value.value : value);
  return Number.isFinite(parsed) ? parsed : null;
};
export const text = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    return 'value' in value ? text(value.value) : JSON.stringify(value);
  }
  return String(value);
};

const duration = (seconds: number) => {
  const s = Math.round(seconds);
  if (s < 60) {
    return `${s}s`;
  }
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) {
    return `${m}m ${String(r).padStart(2, '0')}s`;
  }
  const h = Math.floor(m / 60);
  if (h < 48) {
    return `${h}h ${String(m % 60).padStart(2, '0')}m`;
  }
  return `${(h / 24).toFixed(1)}d`;
};
const compact = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 });

export function formatValue(value: number | null, format: Format = 'count'): string {
  if (value === null || Number.isNaN(value)) {
    return '—';
  }
  switch (format) {
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    case 'number1':
      return value.toFixed(1);
    case 'seconds':
      return duration(value);
    case 'minutes':
      return duration(value * 60);
    case 'hours':
      return duration(value * 3600);
    default:
      return Number.isInteger(value) ? compact.format(value) : value.toFixed(1);
  }
}

/** Percent formats compare in points; everything else relative to the previous value. */
export function delta(current: number | null, previous: number | null, format: Format) {
  if (current === null || previous === null) {
    return null;
  }
  if (format === 'percent') {
    return { kind: 'pts' as const, value: (current - previous) * 100 };
  }
  if (previous === 0) {
    return null;
  }
  return { kind: 'rel' as const, value: ((current - previous) / Math.abs(previous)) * 100 };
}
export const deltaText = (d: ReturnType<typeof delta>) =>
  d ? `${d.value >= 0 ? '+' : ''}${d.value.toFixed(1)}${d.kind === 'pts' ? ' pts' : '%'}` : '';
export const deltaGood = (d: ReturnType<typeof delta>, meaning: Meaning): boolean | null =>
  !d || meaning === 'neutral' ? null : d.value >= 0 === (meaning === 'up');

export function onTarget(value: number | null, item: Item): { on: boolean; text: string } | null {
  if (value === null) {
    return null;
  }
  if (item.target !== undefined) {
    const on = value >= item.target;
    return { on, text: `${on ? 'On target' : 'Short of'} ≥ ${formatValue(item.target, item.format)}` };
  }
  if (item.target_max !== undefined) {
    const on = value <= item.target_max;
    return { on, text: `${on ? 'On target' : 'Short of'} ≤ ${formatValue(item.target_max, item.format)}` };
  }
  return null;
}

/** A metric card's current and comparison figures: the engine returns one aggregated row per window. */
export function metricValues(card: LensCardData | undefined, column: string) {
  const row = card?.rows?.[0];
  const previous = card?.comparison?.rows?.[0];
  return { current: num(row?.[column]), previous: num(previous?.[column]) };
}

/** A ratio card arrives as of / per sums; the engine may also return the ratio under the card id. */
export function ratioValues(card: LensCardData | undefined, of: string, per: string) {
  const read = (row: Record<string, unknown> | undefined) => {
    const top = num(row?.[of]);
    const base = num(row?.[per]);
    const direct = num(row?.ratio ?? row?.value);
    if (direct !== null && (top === null || base === null)) {
      return direct;
    }
    return top === null || base === null || base === 0 ? null : top / base;
  };
  return { current: read(card?.rows?.[0]), previous: read(card?.comparison?.rows?.[0]) };
}
